import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {SaveButtonTrackingService} from '../../../shared/project-step-save-button/save-button-tracking.service';
import {DirectusService, FlattenedProjectStep, ProjectStepDescription} from '@frontend/directus';
import {firstValueFrom} from 'rxjs';
import {CHAPTER_CONFIGS, DEFAULT_CHAPTER_ICON, getChaptersFromConfig} from './chapter-config';
import {TranslocoService} from '@jsverse/transloco';
import {ProjectDataService} from '../../../shared/services/project-data.service';
import {
  ChapterControllerService,
  ChapterResponse,
  ProjectChapterType,
  ProjectStepType,
  StepResponse,
} from '@frontend/shared-api';
import {Chapter, mapBackendStatusToFrontend, Step, StepStatus} from './project-step.types';
import {DEFAULT_SPECIAL_OPTIONS, SpecialOption} from './project-step-state.constants';

enum MenuItemType {
  STEP = 'step',
  SPECIAL = 'special',
}

interface StepActiveState {
  type: MenuItemType.STEP;
  chapterId: ProjectChapterType;
  stepId: ProjectStepType;
}

interface SpecialActiveState {
  type: MenuItemType.SPECIAL;
  optionId: string;
}

type ActiveState = StepActiveState | SpecialActiveState;

@Injectable()
export class ProjectStepStateService {
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly chapterControllerService = inject(ChapterControllerService);
  private readonly saveButtonTrackingService = inject(SaveButtonTrackingService);

  private readonly fallbackImageUrl = '/images/project-chapter-default-bg.jpg';
  private readonly STORAGE_KEY_PREFIX = 'selecthor-active-step';

  private readonly _chapters = signal<Chapter[]>(getChaptersFromConfig(CHAPTER_CONFIGS));

  private readonly _specialOptions = signal<SpecialOption[]>(DEFAULT_SPECIAL_OPTIONS);
  public readonly _projectStepsDirectus = signal<FlattenedProjectStep[]>([]);
  private readonly _stepStatuses = signal<Record<string, StepStatus>>({});
  private readonly _stepLockStatuses = signal<Record<string, boolean>>({});
  public readonly stepDescriptions = signal<ProjectStepDescription[]>([]);
  public currentStepDescription = signal<ProjectStepDescription | null>(null);
  public activeStep = signal<{chapterId: ProjectChapterType; stepId: ProjectStepType} | null>(null);
  readonly descriptionDialogVisible = signal(false);
  public activeSpecialOption = signal<string | null>(null);
  public specialOptions = computed(() => this._specialOptions());
  public loading = signal(false);
  public isCurrentStepDirty = computed(() => this.saveButtonTrackingService.hasSaveButtons());
  public isCurrentStepInProgress = computed(() => {
    const stepKey = this.createStepKey(
      String(this.activeStep()?.chapterId),
      String(this.activeStep()?.stepId),
    );

    return this._stepStatuses()[stepKey] == StepStatus.IN_PROGRESS;
  });
  public isCurrentStepFinished = computed(() => {
    const stepKey = this.createStepKey(
      String(this.activeStep()?.chapterId),
      String(this.activeStep()?.stepId),
    );

    return this._stepStatuses()[stepKey] == StepStatus.COMPLETED;
  });
  public isRfiFinished = computed(() => {
    const steps = this._stepStatuses();
    return Object.keys(this._stepStatuses())
      .filter((key) => key.startsWith(ProjectChapterType.RFI))
      .every((key) => steps[key] === StepStatus.COMPLETED);
  });

  constructor() {
    this.initializeStaticData();
    this.setupProjectEffects();
    this.setupStateRestorationEffect();
    this.setupStatePersistenceEffect();
    this.setupDirtynessTriggerResetEffect();
  }

  public isNavigationConfirmationRequired = computed(() => {
    const currentChapterId = this.activeStep()?.chapterId;
    const currentStepId = this.activeStep()?.stepId;
    if (!currentChapterId || !currentStepId) {
      return false;
    }
    return this.isCurrentStepDirty() && !this.isStepLocked(currentChapterId, currentStepId);
  });

  private initializeStaticData(): void {
    this.loadChaptersAndSteps();
    this.loadProjectStepDescriptions();
  }

  private setupProjectEffects(): void {
    const project$ = this.projectDataService.getProject();

    effect(() => {
      const projectId = project$()?.id;
      if (!projectId) return;

      this.loadStepStatuses(projectId, true);
    });
  }

  private setupStateRestorationEffect(): void {
    const project$ = this.projectDataService.getProject();

    effect(() => {
      const project = project$();
      const loading = this.loading();

      if (!project || loading) return;

      if (!this.activeStep() && !this.activeSpecialOption()) {
        this.restoreActiveState();
      }
    });
  }

  private restoreActiveState(): void {
    const savedState = this.loadCurrentStateFromStorage();

    if (savedState?.type === MenuItemType.SPECIAL) {
      this.setActiveSpecialOption(savedState.optionId);
    } else if (
      savedState?.type === MenuItemType.STEP &&
      this.isValidStep(savedState.chapterId, savedState.stepId)
    ) {
      this.setActiveStep(savedState.chapterId, savedState.stepId);
    } else {
      const firstAccessibleStep = this.findFirstAccessibleStep();
      if (firstAccessibleStep) {
        this.setActiveStep(firstAccessibleStep.chapterId, firstAccessibleStep.stepId);
      }
    }
  }

  private setupStatePersistenceEffect(): void {
    const project$ = this.projectDataService.getProject();

    effect(() => {
      const step = this.activeStep();
      const specialOption = this.activeSpecialOption();
      const project = project$();

      if (!project) return;

      if (specialOption) {
        this.saveCurrentStateToStorage({type: MenuItemType.SPECIAL, optionId: specialOption});
      } else if (step) {
        this.saveCurrentStateToStorage({
          type: MenuItemType.STEP,
          chapterId: step.chapterId,
          stepId: step.stepId,
        });
      }
    });
  }

  private async loadProjectStepDescriptions(): Promise<void> {
    const response = await firstValueFrom(this.directusService.loadProjectStepDescriptions());
    this.stepDescriptions.set(response);
  }

  private async loadChaptersAndSteps(): Promise<void> {
    const projectSteps = await firstValueFrom(this.directusService.loadProjectSteps());
    this._projectStepsDirectus.set(projectSteps);
    this.mergeDirectusStepsAndChapterConfig();
  }

  private mergeDirectusStepsAndChapterConfig(): void {
    const projectSteps = this._projectStepsDirectus();
    const {chapterStepsDirectus, childStepsDirectus} = this.categorizeProjectSteps(projectSteps);
    const currentConfigChapters = this._chapters();

    const updatedChapters = currentConfigChapters.map((configChapter) => {
      const directusChapter = chapterStepsDirectus.find(
        (chapter) => chapter.chapterConfigId === configChapter.configId,
      );

      if (!directusChapter) {
        return configChapter;
      }

      return this.buildMergedChapter(configChapter, directusChapter, childStepsDirectus);
    });

    this._chapters.set(updatedChapters);
  }

  private categorizeProjectSteps(projectSteps: FlattenedProjectStep[]): {
    chapterStepsDirectus: FlattenedProjectStep[];
    childStepsDirectus: FlattenedProjectStep[];
  } {
    return {
      chapterStepsDirectus: projectSteps.filter((step) => !step.parent_step),
      childStepsDirectus: projectSteps.filter((step) => !!step.parent_step),
    };
  }

  private buildMergedChapter(
    configChapter: Chapter,
    directusChapter: FlattenedProjectStep,
    childSteps: FlattenedProjectStep[],
  ): Chapter {
    const chapterTitle = this.extractTranslatedTitle(
      directusChapter,
      `Chapter ${configChapter.configId}`,
    );
    const chapterChildSteps = childSteps.filter((step) => step.parent_step === directusChapter.id);
    const updatedSteps = this.buildMergedSteps(
      configChapter.steps,
      chapterChildSteps,
      configChapter.configId,
    );

    return {
      ...configChapter,
      directusId: directusChapter.id,
      icon: configChapter.icon || DEFAULT_CHAPTER_ICON,
      translatedTitle: chapterTitle,
      steps: updatedSteps,
    };
  }

  private buildMergedSteps(
    configSteps: Step[],
    directusChildSteps: FlattenedProjectStep[],
    parentChapterId: ProjectChapterType,
  ): Step[] {
    return configSteps.map((configStep, stepIndex) => {
      const directusStep = directusChildSteps[stepIndex];
      if (!directusStep) {
        return configStep;
      }

      const stepTitle = this.extractTranslatedTitle(directusStep, `Step ${configStep.configId}`);

      return {
        ...configStep,
        directusId: directusStep.id,
        translatedTitle: stepTitle,
        parentChapterConfigId: parentChapterId,
      };
    });
  }

  private extractTranslatedTitle(directusItem: FlattenedProjectStep, fallback: string): string {
    const currentLang = this.translocoService.getActiveLang();
    const translation = directusItem.translations?.find((t) => t.language === currentLang);
    return translation?.name ?? directusItem.title ?? fallback;
  }

  getChapterBackgroundImage(directusChapter?: FlattenedProjectStep): string {
    if (!directusChapter) return this.fallbackImageUrl;
    return directusChapter.image
      ? this.directusService.getImageUrl(directusChapter.image)
      : this.fallbackImageUrl;
  }

  public chaptersWithStatuses = computed(() => {
    const statuses = this._stepStatuses();
    const chapters = this._chapters();

    return chapters.map((chapter) => ({
      ...chapter,
      steps: chapter.steps.map((step) => {
        const stepKey = this.createStepKey(String(chapter.configId), String(step.configId));

        return {
          ...step,
          status: stepKey ? statuses[stepKey] : step.status,
        };
      }),
    }));
  });

  public completedChaptersCount = computed(
    () =>
      this.chaptersWithStatuses().filter(
        (ch) =>
          ch.steps.every((step) => step.status === StepStatus.COMPLETED) && ch.steps.length > 0,
      ).length,
  );

  public totalChapters = computed(() => this.chaptersWithStatuses().length);

  public progressPercent = computed(() =>
    Math.round((this.completedChaptersCount() / this.totalChapters()) * 100),
  );

  setActiveStep(chapterId: ProjectChapterType, stepId: ProjectStepType): void {
    if (!this.isValidStep(chapterId, stepId)) {
      return;
    }

    this.activeStep.set({chapterId, stepId});
  }

  isStepLocked(chapterId: ProjectChapterType, stepId: ProjectStepType): boolean {
    const key = this.createStepKey(String(chapterId), String(stepId));
    return this._stepLockStatuses()[key] ?? false;
  }

  setActiveSpecialOption(optionId: string): void {
    this.activeSpecialOption.set(optionId);
    this.activeStep.set(null);
  }

  clearActiveSpecialOption(): void {
    this.activeSpecialOption.set(null);
  }

  private updateStepStatuses(chapters: ChapterResponse[]): void {
    if (!chapters) return;

    const statuses: Record<string, StepStatus> = {};
    const lockStatuses: Record<string, boolean> = {};

    chapters.forEach((chapter: ChapterResponse) => {
      chapter.steps.forEach((step: StepResponse) => {
        const key = this.createStepKey(chapter.title, step.title); // BE title = chapterConfigId
        statuses[key] = mapBackendStatusToFrontend(step.progress);
        lockStatuses[key] = step.isLocked;
      });
    });

    this._stepStatuses.set(statuses);
    this._stepLockStatuses.set(lockStatuses);
  }

  async loadStepStatuses(projectId: number, withReset = false): Promise<void> {
    if (withReset) {
      this.loading.set(true);
      this._stepStatuses.set({});
      this._stepLockStatuses.set({});
    }

    try {
      const chapterResponse = await firstValueFrom(
        this.chapterControllerService.getChaptersForProject({projectId}),
      );
      this.updateStepStatuses(chapterResponse);
    } finally {
      if (withReset) this.loading.set(false);
    }
  }

  openStepDescription(directusId?: string): void {
    const id = directusId ?? this.activeChapterDirectusId();
    if (!id) return;

    const description = this.stepDescriptions().find((desc) => desc.projectStep === id);
    this.currentStepDescription.set(description ?? null);
    this.descriptionDialogVisible.set(true);
  }

  closeStepDescription(): void {
    this.descriptionDialogVisible.set(false);
  }

  readonly getCurrentStepDescriptionTitle = computed(() => {
    const currentDesc = this.currentStepDescription();
    const chapterList = this.chaptersWithStatuses();

    if (!currentDesc) return null;

    const chapter = chapterList.find((c) => c.directusId === currentDesc.projectStep);
    return chapter?.translatedTitle ?? null;
  });

  private readonly activeChapter = computed(() => {
    const current = this.activeStep();
    return current
      ? (this._chapters().find((c) => c.configId === current.chapterId) ?? null)
      : null;
  });

  private readonly activeChapterDirectusId = computed(
    () => this.activeChapter()?.directusId ?? null,
  );

  readonly activeChapterBackgroundImage = computed(() => {
    const directusId = this.activeChapterDirectusId();
    if (!directusId) return this.fallbackImageUrl;

    const directusChapter = this._projectStepsDirectus().find(
      (step) => !step.parent_step && step.id === directusId,
    );

    return directusChapter
      ? this.getChapterBackgroundImage(directusChapter)
      : this.fallbackImageUrl;
  });

  isChapterCompleted(chapterId: ProjectChapterType): boolean {
    const chapter = this.chaptersWithStatuses().find((c) => c.configId === chapterId);
    return chapter?.steps.every((step) => step.status === StepStatus.COMPLETED) ?? false;
  }

  areStepsCompleted(
    stepRefs: Array<{chapterId: ProjectChapterType; stepId: ProjectStepType}>,
  ): boolean {
    const chapters = this.chaptersWithStatuses();
    return stepRefs.every(({chapterId, stepId}) => {
      const chapter = chapters.find((c: Chapter) => c.configId === chapterId);
      const step = chapter?.steps.find((s: Step) => s.configId === stepId);
      return step?.status === StepStatus.COMPLETED;
    });
  }

  private saveCurrentStateToStorage(state: ActiveState): void {
    const project = this.projectDataService.getProject()();
    if (!project) return;

    const storageKey = `${this.STORAGE_KEY_PREFIX}-${project.id}`;

    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save current state:', error);
    }
  }

  private loadCurrentStateFromStorage(): ActiveState | null {
    const project = this.projectDataService.getProject()();
    if (!project) return null;

    const storageKey = `${this.STORAGE_KEY_PREFIX}-${project.id}`;

    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to load current state:', error);
      return null;
    }
  }

  private isValidStep(chapterId: ProjectChapterType, stepId: ProjectStepType): boolean {
    const chapters = this._chapters();
    const chapter = chapters.find((c) => c.configId === chapterId);
    const step = chapter?.steps.find((s) => s.configId === stepId);

    return !(!chapter || !step);
  }

  private findFirstAccessibleStep(): {
    chapterId: ProjectChapterType;
    stepId: ProjectStepType;
  } | null {
    const chapters = this._chapters();

    for (const chapter of chapters) {
      for (const step of chapter.steps) {
        if (this.isValidStep(chapter.configId, step.configId)) {
          return {chapterId: chapter.configId, stepId: step.configId};
        }
      }
    }

    return null;
  }

  private setupDirtynessTriggerResetEffect() {
    effect(() => {
      this.activeStep();
      this.saveButtonTrackingService.clearSaveButtons();
    });
  }

  private createStepKey(chapterConfigId: string, stepConfigId: string): string {
    return `${chapterConfigId}-${stepConfigId}`;
  }
}
